import React, { useEffect, useState } from "react";
import { PageHeader } from "@/components/PageHeader";
import { toast } from "sonner";
import { Truck, Check, Upload, Search, MapPin, Home, Home as HomeIcon } from "lucide-react";
import { Input } from "@/components/ui/input";

export function GiriListView({ giri, user, authAxios, onSelectGiro, onReloadGiri, onJumpToStop }) {
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    const q = search.trim();
    if (q.length < 2) { setResults([]); return; }
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const r = await authAxios.get(`/giri/search-client?q=${encodeURIComponent(q)}`);
        setResults(r.data || []);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [search, authAxios]);

  const inSearch = search.trim().length >= 2;

  return (
    <div className="min-h-screen bg-[#09090b]">
      <main className="p-4 sm:p-6">
        <PageHeader title="Giri" />
        <h1 className="font-heading text-2xl font-bold text-white mb-1" data-testid="giri-title">Giri Presa Ordine</h1>
        <p className="text-zinc-400 text-sm mb-4">Seleziona il giro del giorno</p>

        {/* Client search */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
          <Input
            placeholder="Cerca cliente per trovarlo in un giro..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-[#18181b] border-white/10 h-10"
            data-testid="giri-client-search"
          />
        </div>

        {inSearch && (
          <div className="mb-6 space-y-1" data-testid="giri-search-results">
            {searching && <p className="text-zinc-500 text-xs text-center py-2">Ricerca in corso...</p>}
            {!searching && results.length === 0 && (
              <p className="text-zinc-500 text-xs text-center py-4">Nessun cliente trovato per "{search}"</p>
            )}
            {results.map((s) => {
              const hasDest = s.denominaz_1 && s.denominaz_1 !== s.denominaz;
              // denominaz = nome destinazione; denominaz_1 = ragione sociale cliente
              const name = s.denominaz || s.ragione_sociale || s.denominaz_1;
              const clientLabel = hasDest ? s.denominaz_1 : null;
              const addr = [s.indirizzo, s.localita].filter(Boolean).join(", ");
              const isDone = !!s.completed_order_id;
              return (
                <button
                  key={s.id}
                  onClick={() => onJumpToStop(s)}
                  data-testid={`search-result-${s.id}`}
                  className="w-full p-3 rounded-lg border border-white/5 hover:border-amber-500/40 bg-[#18181b] hover:bg-amber-500/5 text-left transition-colors flex items-start gap-3"
                >
                  <div className="w-9 h-9 rounded-md bg-amber-500/10 border border-amber-500/30 flex flex-col items-center justify-center shrink-0">
                    <span className="text-amber-400 font-mono font-bold text-[11px] leading-tight">
                      {s.cod_agente}-{s.cod_giorno}
                    </span>
                    <span className="text-zinc-500 text-[8px] uppercase tracking-wider leading-none">#{s.num_giro}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      {hasDest && <HomeIcon className="w-3 h-3 text-cyan-400 shrink-0" />}
                      <p className="text-white text-sm font-medium truncate">{name}</p>
                      {isDone && <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0" />}
                    </div>
                    {hasDest && (
                      <p className="text-amber-400 text-[10px] font-bold truncate">cliente: {clientLabel}</p>
                    )}
                    {addr && (
                      <div className="flex items-center gap-1 text-[10px] text-zinc-400 mt-0.5 truncate">
                        <MapPin className="w-3 h-3 shrink-0" />
                        <span className="truncate">{addr}</span>
                      </div>
                    )}
                    <p className="text-zinc-600 text-[9px] mt-0.5">
                      {s.giorno_label} · {s.cod_cliente}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {user?.role === "admin" && !inSearch && (
          <label className="inline-flex items-center gap-2 mb-6 cursor-pointer">
            <input type="file" accept=".xls,.xlsx,.csv,.txt" className="hidden" onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              const t = toast.loading("Importazione giri...");
              try {
                const fd = new FormData(); fd.append("file", file);
                const r = await authAxios.post("/giri/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
                toast.success(r.data.message, { id: t });
                const res = await authAxios.get("/giri");
                onReloadGiri(res.data);
              } catch (err) { toast.error(err.response?.data?.detail || "Errore", { id: t }); }
              e.target.value = "";
            }} />
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium transition-colors">
              <Upload className="w-4 h-4" /> Importa Giri (.xls/.xlsx/.csv)
            </span>
          </label>
        )}

        {!inSearch && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {giri.map(g => {
              const allDone = g.completate > 0 && g.completate >= g.fermate;
              return (
                <button key={`${g.cod_agente}-${g.cod_giorno}`}
                  data-testid={`giro-btn-${g.cod_agente}-${g.cod_giorno}`}
                  onClick={() => onSelectGiro(g.cod_agente, g.cod_giorno)}
                  className={`p-4 rounded-xl border text-left transition-all hover:scale-[1.02] ${
                    allDone
                      ? "border-emerald-500/30 bg-emerald-500/5 hover:border-emerald-500/50"
                      : "border-white/10 hover:border-amber-500/40 bg-[#18181b]"
                  }`}>
                  <div className="flex items-center gap-2 mb-2">
                    <Truck className={`w-4 h-4 ${allDone ? "text-emerald-400" : "text-amber-400"}`} />
                    <span className="text-white font-bold text-sm">{g.cod_agente}-{g.cod_giorno}</span>
                    {allDone && <Check className="w-3.5 h-3.5 text-emerald-400 ml-auto" />}
                  </div>
                  <p className="text-zinc-400 text-xs">{g.giorno_label}</p>
                  <div className="flex items-baseline gap-1.5 mt-1">
                    <p className={`text-xs font-mono ${allDone ? "text-emerald-400" : "text-amber-400"}`}>{g.num_clienti} clienti</p>
                    {g.completate > 0 && (
                      <span className="text-emerald-400 text-[10px] font-mono">• {g.completate}/{g.fermate} fatte</span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
